/** Human-readable message for a failed creator PATCH.
 *
 * The creators route answers with `_bad(error, **extra)`, so the body is a
 * machine `error` code plus whatever context that particular failure carries.
 * For a rejected profile link that context is `reason`, which says WHICH rule
 * was broken — and it used to be dropped on the floor, leaving the operator
 * staring at a bare `invalid_custom_link` with four possible causes.
 *
 * Keep this in sync with `_validate_custom_link` in
 * `backend/support/routes/creators.py`.
 */

// Mirrors CUSTOM_LINK_MIN_LEN / CUSTOM_LINK_MAX_LEN / _CUSTOM_LINK_RE.
const CUSTOM_LINK_REASONS: Record<string, string> = {
  too_short: "Profile link must be at least 3 characters.",
  too_long: "Profile link must be at most 50 characters.",
  invalid: "Profile link may only contain lowercase letters, numbers and hyphens.",
  reserved: "That profile link is reserved — choose another.",
};

// Which editable field a failure points at, for codes that imply one rather
// than naming it. Codes that DO name it (`unknown_field`, `invalid_list_field`)
// send a `field` key, which wins over this map.
const FIELD_BY_CODE: Record<string, string> = {
  invalid_custom_link: "custom_link",
  custom_link_taken: "custom_link",
  invalid_status: "status",
  invalid_origin: "origin",
  invalid_credits: "credits",
  invalid_verified: "verified",
  invalid_origin_event_id: "origin_event_id",
  invalid_referral_terms: "referral_terms",
  invalid_profile_picture: "profile_picture",
  invalid_s3_key: "profile_picture",
  upload_not_found: "profile_picture",
  invalid_pii: "pii",
  missing_reason: "reason",
};

type ApiErrorBody = {
  error?: unknown;
  reason?: unknown;
  field?: unknown;
  detail?: unknown;
};

/** The offending field, so the UI can outline it — null when the failure
 *  doesn't point at one. */
export function creatorSaveError(
  e: unknown,
  fallback = "Failed to save",
): { message: string; field: string | null } {
  const data = (e as { response?: { data?: ApiErrorBody } })?.response?.data;
  const explicit = typeof data?.field === "string" ? data.field : "";
  const code = typeof data?.error === "string" ? data.error : "";
  return {
    message: creatorSaveErrorMessage(e, fallback),
    field: explicit || FIELD_BY_CODE[code] || null,
  };
}

export function creatorSaveErrorMessage(e: unknown, fallback = "Failed to save"): string {
  const data = (e as { response?: { data?: ApiErrorBody } })?.response?.data;
  if (!data) return fallback;

  // A spelled-out backend message always wins.
  if (typeof data.detail === "string" && data.detail.trim()) return data.detail;

  const code = typeof data.error === "string" ? data.error : "";
  if (!code) return fallback;

  if (code === "custom_link_taken") {
    return "That profile link is already taken by another profile.";
  }

  if (code === "invalid_custom_link") {
    const reason = typeof data.reason === "string" ? data.reason : "";
    // An unknown reason still surfaces rather than vanishing, so a rule added
    // backend-side is visible here before anyone updates this map.
    return CUSTOM_LINK_REASONS[reason] ?? `${code} (${reason || "unknown reason"})`;
  }

  const field = typeof data.field === "string" ? data.field : "";
  return field ? `${code} (${field})` : code;
}
